// Modal "Aplicar foto em produtos" — aberto pelo Acervo da Gestao de Fotos.
// Busca produtos por codigo/descricao, marca varios, e aplica a MESMA foto em
// todos de uma vez (POST /api/produtos/foto-aplicar-em-produtos). Como o master
// e vinculado ao arquivo da foto, os produtos herdam tambem o master/selo.
import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { api } from '../../lib/api';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { Modal } from '../../components/Modal';

interface ProdutoBusca {
  id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  status?: string | null;
}

interface Props {
  // Foto que sera aplicada nos produtos selecionados.
  fotoUrl: string;
  fotoNome: string | null;
  onFechar: () => void;
  // Avisado apos aplicar (qtd de produtos atualizados) — o pai recarrega o acervo.
  onAplicado: (qtd: number) => void;
}

export function ModalAplicarFotoProdutos({ fotoUrl, fotoNome, onFechar, onAplicado }: Props) {
  const dialog = useDialog();
  const [busca, setBusca] = useState('');
  const [resultados, setResultados] = useState<ProdutoBusca[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [selecionados, setSelecionados] = useState<Map<number, ProdutoBusca>>(new Map());
  const [aplicando, setAplicando] = useState(false);

  // Busca produtos por codigo/descricao com debounce.
  useEffect(() => {
    const t = busca.trim();
    if (t.length < 2) {
      setResultados([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    const timer = setTimeout(() => {
      api
        .get<ProdutoBusca[]>(`/api/produtos?q=${encodeURIComponent(t)}&com_inativos=1`)
        .then((r) => setResultados(r))
        .catch(() => setResultados([]))
        .finally(() => setCarregando(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  function toggle(p: ProdutoBusca) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    setSelecionados((prev) => {
      const n = new Map(prev);
      if (n.has(p.id)) n.delete(p.id);
      else n.set(p.id, p);
      return n;
    });
  }

  // Todos os resultados da busca atual estao marcados? (inativo nao entra: nao pode ser marcado)
  const marcaveis = resultados.filter((p) => !produtoInativo(p));
  const todosMarcados = marcaveis.length > 0 && marcaveis.every((p) => selecionados.has(p.id));

  // Marca/desmarca todos os resultados da busca atual de uma vez.
  function toggleTodos() {
    setSelecionados((prev) => {
      const n = new Map(prev);
      if (marcaveis.every((p) => n.has(p.id))) {
        for (const p of marcaveis) n.delete(p.id);
      } else {
        for (const p of marcaveis) n.set(p.id, p);
      }
      return n;
    });
  }

  async function aplicar() {
    if (selecionados.size === 0) return;
    setAplicando(true);
    try {
      const r = await api.post<{ atualizados: number; fotos_removidas: number }>(
        '/api/produtos/foto-aplicar-em-produtos',
        { foto_url: fotoUrl, produto_ids: [...selecionados.keys()] },
      );
      const extra = r.fotos_removidas > 0 ? ` · ${r.fotos_removidas} foto(s) antiga(s) removida(s)` : '';
      dialog.toast(`Foto aplicada em ${r.atualizados} produto(s)${extra}`, { tipo: 'sucesso' });
      onAplicado(r.atualizados);
    } catch (e: any) {
      dialog.toast(e.message ?? 'Falha ao aplicar a foto', { tipo: 'erro' });
    } finally {
      setAplicando(false);
    }
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo="Aplicar foto em produtos"
      largura={640}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={aplicando}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primario"
            onClick={aplicar}
            disabled={aplicando || selecionados.size === 0}
          >
            {aplicando
              ? 'Aplicando...'
              : `Aplicar em ${selecionados.size} produto${selecionados.size === 1 ? '' : 's'}`}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '62vh', gap: 10 }}>
        {/* Foto que sera aplicada */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          <img
            src={fotoUrl}
            alt=""
            style={{
              width: 56,
              height: 56,
              objectFit: 'contain',
              background: '#fff',
              borderRadius: 6,
              border: '1px solid var(--panel-border, #e5e7eb)',
            }}
          />
          <div style={{ fontSize: 13, color: 'var(--text-muted, #6b7280)' }}>
            Os produtos marcados abaixo vão passar a usar <strong>esta foto</strong>
            {fotoNome ? ` (${fotoNome})` : ''}. A foto antiga de cada um é substituída — e, se
            ela ficar sem nenhum produto, é removida do acervo.
          </div>
        </div>

        {/* Busca */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Search
            size={16}
            style={{ position: 'absolute', left: 10, top: 11, color: 'var(--text-muted, #6b7280)' }}
          />
          <input
            type="text"
            autoFocus
            placeholder="Buscar produto por codigo ou descricao..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 10px 9px 34px',
              border: '1px solid var(--input-border, #d1d5db)',
              borderRadius: 6,
              background: 'var(--input-bg)',
              color: 'var(--text-color)',
              fontSize: 14,
            }}
          />
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
            {selecionados.size > 0
              ? `${selecionados.size} produto(s) selecionado(s)`
              : 'Marque os produtos que vão receber a foto.'}
          </span>
          {resultados.length > 0 && (
            <button
              type="button"
              onClick={toggleTodos}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--primary)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 700,
                whiteSpace: 'nowrap',
              }}
            >
              {todosMarcados ? 'Desmarcar todos' : 'Marcar todos'}
            </button>
          )}
        </div>

        {/* Lista de resultados */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}>
          {carregando ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
              Buscando...
            </div>
          ) : busca.trim().length < 2 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
              Digite ao menos 2 letras pra buscar produtos.
            </div>
          ) : resultados.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted, #6b7280)' }}>
              Nenhum produto encontrado.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {resultados.map((p) => {
                const marcado = selecionados.has(p.id);
                return (
                  <div
                    key={p.id}
                    onClick={() => toggle(p)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 8px',
                      borderRadius: 6,
                      cursor: 'pointer',
                      border: '1px solid var(--panel-border, #e5e7eb)',
                      background: marcado ? '#fef9c3' : 'var(--input-bg, #fff)',
                    }}
                  >
                    <input type="checkbox" checked={marcado} readOnly style={{ flexShrink: 0 }} />
                    <img
                      src={p.foto_url || ''}
                      alt=""
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        background: '#fff',
                        borderRadius: 4,
                        border: '1px solid var(--panel-border, #e5e7eb)',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: 12,
                        color: 'var(--text-muted, #6b7280)',
                        flexShrink: 0,
                      }}
                    >
                      {p.codigo_interno || '—'}
                    </span>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        color: marcado ? '#000' : 'var(--text-color)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {produtoInativo(p) && <SeloInativo />}{p.descricao}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
